/**
 * Claim-window transitions: Ron, Pung, Chi, and the pass-through that advances
 * play when nobody claims. Split out of `GameState.ts` for the 200-line cap;
 * re-exported from there as the public API. Pure — each returns a NEW state.
 */

import { isSameKind, sortTiles, type Meld, type Seat } from './types.js';
import { canPung, canRon, chiOptions, type ChiOption } from './Claims.js';
import { resolveAddedKong } from './kongTransitions.js';
import {
  PUNG_HAND_TILES,
  applyClaim,
  nextSeat,
  requireDiscard,
  requirePhase,
  type MahjongState,
} from './stateCore.js';

/** Draws the front of the wall for `seat`; an empty wall ends the hand as exhausted. */
function drawForSeat(state: MahjongState, seat: Seat): MahjongState {
  if (state.wall.length === 0) {
    return {
      ...state,
      phase: 'hand-end',
      lastDiscard: null,
      drawnTile: null,
      result: { kind: 'exhausted' },
    };
  }
  const [drawn, ...rest] = state.wall;
  if (drawn === undefined) throw new Error('unreachable: non-empty wall yielded no tile');
  return {
    ...state,
    hands: state.hands.map((h, i) => (i === seat ? [...h, drawn] : h)),
    wall: rest,
    turn: seat,
    phase: 'awaiting-discard',
    lastDiscard: null,
    drawnTile: drawn,
  };
}

/** A seat declares a win off the open discard. */
export function declareRon(state: MahjongState, seat: Seat): MahjongState {
  requirePhase(state, 'claim-window');
  const discard = requireDiscard(state);
  if (seat === discard.from) throw new Error('the discarder cannot ron their own tile');
  const hand = state.hands[seat] ?? [];
  const melds = state.melds[seat] ?? [];
  if (!canRon(hand, melds, discard.tile, state.ruleset)) {
    throw new Error(`seat ${seat} cannot ron this discard`);
  }
  return {
    ...state,
    phase: 'hand-end',
    result: { kind: 'ron', winner: seat, winningTile: discard.tile, from: discard.from },
  };
}

/** A seat calls Pung on the discard; it then discards without drawing. */
export function claimPung(state: MahjongState, seat: Seat): MahjongState {
  requirePhase(state, 'claim-window');
  const discard = requireDiscard(state);
  if (seat === discard.from) throw new Error('the discarder cannot pung their own tile');
  const hand = state.hands[seat] ?? [];
  if (!canPung(hand, discard.tile)) throw new Error(`seat ${seat} cannot pung this discard`);
  const pair = hand.filter((t) => isSameKind(t, discard.tile)).slice(0, PUNG_HAND_TILES);
  const meld: Meld = { type: 'pung', tiles: sortTiles([...pair, discard.tile]), concealed: false };
  const usedIds = new Set(pair.map((t) => t.id));
  return applyClaim(state, seat, discard.from, discard.tile.id, meld, usedIds);
}

/** A seat calls Chi on the left-neighbour discard; it then discards without drawing. */
export function claimChi(state: MahjongState, seat: Seat, option: ChiOption): MahjongState {
  requirePhase(state, 'claim-window');
  const discard = requireDiscard(state);
  if (seat !== nextSeat(discard.from)) throw new Error('chi is only allowed from the left neighbour');
  const hand = state.hands[seat] ?? [];
  const optionIds = new Set(option.tiles.map((t) => t.id));
  const legal = chiOptions(hand, discard.tile).some((o) => o.tiles.every((t) => optionIds.has(t.id)));
  if (!legal) throw new Error(`seat ${seat} cannot chi with the given option`);
  const meld: Meld = { type: 'chi', tiles: sortTiles([...option.tiles, discard.tile]), concealed: false };
  return applyClaim(state, seat, discard.from, discard.tile.id, meld, optionIds);
}

/**
 * Nobody claimed the open claim window: the next seat in order draws (or the
 * hand exhausts) — unless this window was actually a robbing-the-kong window
 * (an added-kong declaration pending), in which case the kong resolves
 * instead. One single "resolve when nobody claims" entry point regardless of
 * which case it is.
 */
export function passClaims(state: MahjongState): MahjongState {
  requirePhase(state, 'claim-window');
  const discard = requireDiscard(state);
  if (state.pendingAddedKongSeat !== null) return resolveAddedKong(state);
  return drawForSeat(state, nextSeat(discard.from));
}
